import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useEffect, useState } from "react";

import { listCourts } from "@/libs/facilityApi";

import { DenseButton, STAFF_FONT, tone } from "./ui";

export type FacilityCourt = { court_id: string; name: string; status?: string };

/** Enabled courts, whether the read failed, and a retry. */
export function useFacilityCourtsState(facilityId: string | null) {
  const [courts, setCourts] = useState<FacilityCourt[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!facilityId) return;
    let active = true;
    setFailed(false);
    void listCourts(facilityId)
      .then((rows) => {
        if (active) setCourts(rows.filter((court) => (court.status ?? "enabled") === "enabled"));
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [facilityId, attempt]);
  return { courts, failed, retry: () => setAttempt((n) => n + 1) };
}

export function useFacilityCourts(facilityId: string | null) {
  return useFacilityCourtsState(facilityId).courts;
}

/** "All 12 courts at <facility> [Change]" and a checklist of courts. */
export function CourtsPicker({
  courts,
  value,
  onChange,
  facilityName,
  phone,
}: {
  courts: FacilityCourt[];
  value: string[];
  onChange: (ids: string[]) => void;
  facilityName: string;
  phone?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const all = courts.length > 0 && value.length === courts.length;
  const summary = all ? (
    <>
      All <b>{courts.length} courts</b> at {facilityName}
    </>
  ) : (
    <>
      <b>
        {value.length} of {courts.length} courts
      </b>{" "}
      at {facilityName}
    </>
  );
  const openDialog = () => {
    setDraft(value);
    setOpen(true);
  };
  return (
    <>
      {phone ? (
        <DenseButton onClick={openDialog} size="lg" sx={{ flex: 1, height: 44 }}>
          {all ? `All ${courts.length} courts ›` : `${value.length} of ${courts.length} courts ›`}
        </DenseButton>
      ) : (
        <>
          <Box component="span" sx={{ fontSize: 12.5, color: tone.ink2 }}>
            {summary}
          </Box>
          <DenseButton onClick={openDialog} size="sm">
            Change
          </DenseButton>
        </>
      )}
      <Dialog onClose={() => setOpen(false)} open={open} slotProps={{ paper: { sx: { width: 420, borderRadius: "12px", fontFamily: STAFF_FONT } } }}>
        <Box sx={{ p: "14px 18px", borderBottom: `1px solid ${tone.divider}` }}>
          <Box component="b" sx={{ fontSize: 15 }}>
            Courts for this tournament
          </Box>
          <Box sx={{ fontSize: 12, color: tone.subtle, mt: "2px" }}>
            Courts you leave out stay bookable. The tournament holds the rest during play hours once registration opens.
          </Box>
        </Box>
        <Box sx={{ p: "8px 18px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px", maxHeight: 360, overflowY: "auto" }}>
          {courts.map((court) => (
            <Box component="label" key={court.court_id} sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13, minHeight: 32 }}>
              <input
                checked={draft.includes(court.court_id)}
                onChange={(event) =>
                  setDraft((current) =>
                    event.target.checked ? [...current, court.court_id] : current.filter((id) => id !== court.court_id),
                  )
                }
                style={{ accentColor: tone.brand, width: 15, height: 15 }}
                type="checkbox"
              />
              {court.name}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, p: "12px 18px", borderTop: `1px solid ${tone.divider}`, bgcolor: tone.page }}>
          <DenseButton onClick={() => setDraft(courts.map((court) => court.court_id))} variant="link">
            Use all courts
          </DenseButton>
          <Box sx={{ flexGrow: 1 }} />
          <DenseButton onClick={() => setOpen(false)}>Cancel</DenseButton>
          <DenseButton
            disabled={draft.length === 0}
            onClick={() => {
              onChange(courts.map((court) => court.court_id).filter((id) => draft.includes(id)));
              setOpen(false);
            }}
            variant="primary"
          >
            Use {draft.length} court{draft.length === 1 ? "" : "s"}
          </DenseButton>
        </Box>
      </Dialog>
    </>
  );
}
